import { useRef } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ArrowRightIcon, CheckIcon, ClockIcon, XIcon } from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import util from "utils/utils";

import { ReadonlyEmail } from "./AnnouncementManager/EmailPreviewModal";
import { UserChip } from "./Chips";

const CenterBreadcrumb = styled.div`
  display: flex;
  align-items: center;
`;

const Chip = ({
  image,
  name,
  to,
  fallbackSrc,
}: {
  image?: string;
  name?: string;
  to: string;
  fallbackSrc?: string;
}) => (
  <Link style={{ display: "flex", alignItems: "center" }} to={to}>
    <ImageWithFallback
      avatar
      src={image}
      fallbackSrc={fallbackSrc}
      style={{ objectFit: "contain" }}
    />
    <span>{name}</span>
  </Link>
);

type EmailLogModalProps = {
  log:
    | OpenAPI.GET<"/organisations/{org_id}/communication/emails/logs/{log_id}">["response"]["log"]
    | null;
  onClose: () => void;
};

export const EmailLogModal = ({ log, onClose }: EmailLogModalProps) => {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);

  if (!log) return;

  return (
    <Modal open onClose={onClose} closeOnDimmerClick closeOnEscape>
      <ModalHeader>Email overview</ModalHeader>
      <ModalContent scrolling>
        <ModalDescription>
          <H4>The following email was sent on {formatDateAt(log?.sentAt)}</H4>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>Subject</TableCell>
                <TableCell>{log?.subject}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell collapsing>Notification type</TableCell>
                <TableCell>{log?.notificationType}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 15 }}
                  >
                    {log?.ownerUser ? <UserChip user={log?.ownerUser} /> : null}
                    <p>
                      Email:
                      <br />
                      <a href={`mailto:${log?.recipientEmail}`}>
                        {log?.recipientEmail}
                      </a>
                    </p>
                  </div>
                </TableCell>
              </TableRow>
              {log?.ownerChallenge ? (
                <TableRow>
                  <TableCell>
                    {t("common:capitalise", { key: "generic.challenge" })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      name={log?.ownerChallenge?.name}
                      image={log?.ownerChallenge?.coverImageUrl}
                      fallbackSrc={util.challengeImage(undefined)}
                      to={`/challenges/${log?.ownerChallenge?._id}`}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {log?.ownerIdea ? (
                <TableRow>
                  <TableCell>
                    {t("common:capitalise", { key: "generic.idea" })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      name={log?.ownerIdea?.name}
                      image={log?.ownerIdea?.coverImageUrl}
                      fallbackSrc={util.ideaCoverImage(undefined)}
                      to={`/ideas/${log?.ownerIdea?._id}`}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {log?.ownerGroup ? (
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>
                    <Chip
                      name={log?.ownerGroup?.name}
                      image={log?.ownerGroup?.coverImageUrl}
                      fallbackSrc={util.groupCoverImage(undefined)}
                      to={`/groups/${log?.ownerGroup?._id}`}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <H4>Delivery status:</H4>
          <Message color="blue">
            The status of emails may not be reflected in real-time.
            Additionally, be aware that users may be using software which blocks
            certain status updates (such as detecting opening) from registering
            in our system.
            <br />
            <br />
            You can hover over individual status events to learn more about
            their meaning.
          </Message>
          <div style={{ display: "flex" }}>
            <Popup
              trigger={
                <CenterBreadcrumb>
                  <div>
                    <strong>Generated</strong>
                    <br />
                    {formatDateAt(log?.sentAt)}
                  </div>
                </CenterBreadcrumb>
              }
              position="top center"
            >
              The email has been created and was passed on to the mail server to
              be processed.
            </Popup>
            {log?.events?.processed ? (
              <Popup
                trigger={
                  <CenterBreadcrumb>
                    <Icon icon={ArrowRightIcon} />
                    <div>
                      <strong>Processed</strong>
                      <br />
                      {formatDateAt(log?.events?.processedAt)}
                    </div>
                  </CenterBreadcrumb>
                }
                position="top center"
              >
                The email has been processed by the mail server and is ready to
                be sent.
              </Popup>
            ) : null}
            {log?.events?.deferred ? (
              <Popup
                trigger={
                  <CenterBreadcrumb>
                    <Icon icon={ArrowRightIcon} />
                    <div>
                      <strong>
                        Deferred{" "}
                        {!log?.events?.delivered ? (
                          <Icon icon={ClockIcon} color="grey" />
                        ) : null}
                      </strong>
                      <br />
                      {formatDateAt(log?.events?.deferredAt)}
                    </div>
                  </CenterBreadcrumb>
                }
                position="top center"
              >
                The mail server temporarily rejected this message. Re-delivery
                will be attempted shortly. If this issue persists please contact
                us.
              </Popup>
            ) : null}
            {log?.events?.delivered ? (
              <Popup
                trigger={
                  <CenterBreadcrumb>
                    <Icon icon={ArrowRightIcon} />
                    <div>
                      <strong>
                        Delivered{" "}
                        {!log?.events?.opened ? (
                          <Icon icon={CheckIcon} color="green" />
                        ) : null}
                      </strong>
                      <br />
                      {formatDateAt(log?.events?.deliveredAt)}
                    </div>
                  </CenterBreadcrumb>
                }
                position="top center"
              >
                {
                  "The email has succesfully been delivered to the user's inbox."
                }
              </Popup>
            ) : null}
            {log?.events?.opened ? (
              <Popup
                trigger={
                  <CenterBreadcrumb>
                    <Icon icon={ArrowRightIcon} />
                    <div>
                      <strong>
                        Opened <Icon icon={CheckIcon} color="green" />
                      </strong>
                      <br />
                      {formatDateAt(log?.events?.openedAt)}
                    </div>
                  </CenterBreadcrumb>
                }
                position="top center"
              >
                {
                  "The user has opened the email. Please note that depending on the user's email client and privacy settings this may not be accurate and/or may not be reported at all."
                }
              </Popup>
            ) : null}
            {log?.events?.bounce || log?.events?.dropped ? (
              <Popup
                trigger={
                  <CenterBreadcrumb>
                    <Icon icon={ArrowRightIcon} />
                    <div>
                      <strong>
                        Not delivered <Icon icon={XIcon} color="red" />
                      </strong>
                      <br />
                      {formatDateAt(
                        log?.events?.bounceAt || log?.events?.droppedAt,
                      )}
                    </div>
                  </CenterBreadcrumb>
                }
                position="top center"
              >
                {
                  "It looks like an issue prevented this email from being sent correctly. Please double check the recipient's email address and try again. If this issue persists please contact us."
                }
              </Popup>
            ) : null}
          </div>
          {log.html ? (
            <>
              <H4>Message content:</H4>
              <Message color="orange">
                Please be aware that this email has already been delivered, you
                should not interact with the contents of this email and use it
                purely as a source of information.
              </Message>
              <ReadonlyEmail
                onClickCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <iframe
                  scrolling="no"
                  ref={frameRef}
                  onLoad={() => {
                    if (!frameRef.current) {
                      return;
                    }
                    frameRef.current.style.height = `${frameRef.current?.contentWindow?.document?.body?.scrollHeight}px`;
                  }}
                  srcDoc={
                    log.html.replace(
                      'src="cid:bannerImage"',
                      'src="cid:bannerImage" height="150"',
                    ) +
                    `<style>body { pointer-events: none; } table { max-width: 500px !important; } img { max-width: 460px !important; } </style>`
                  }
                  title="Email content"
                />
              </ReadonlyEmail>
            </>
          ) : null}
        </ModalDescription>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => onClose()}>Close</Button>
      </ModalActions>
    </Modal>
  );
};
